import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "./CharacterDetails.css"; // Updated to use the new CSS file
import "../components/FavoriteCharacterButton.css";

// Shared components
import LoadingSpinner from "../components/shared/LoadingSpinner";
import ErrorMessage from "../components/shared/ErrorMessage";
import MediaTabs from "../components/shared/MediaTabs";

// Media detail components
import MediaHero from "../components/mediaDetails/MediaHero";
import MediaHeader from "../components/mediaDetails/MediaHeader";
import StatsGrid from "../components/mediaDetails/StatsGrid";

// Character-specific components
import CharacterAbout from "../components/characterDetails/CharacterAbout";
import CharacterAppearances from "../components/characterDetails/CharacterAppearances";
import CharacterVoiceActors from "../components/characterDetails/CharacterVoiceActors";
import FavoriteCharacterButton from "../components/FavoriteCharacterButton";

// Get API URL from environment variables
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';

// Fetch with retry functionality
const fetchWithRetry = async (url, options = {}, retries = 3, delay = 1000) => {
  try {
    const response = await fetch(url, options);
    
    // If rate limited and retries left, wait and try again
    if (response.status === 429 && retries > 0) {
      console.log(`Rate limited, retrying in ${delay}ms... (${retries} retries left)`);
      await new Promise(resolve => setTimeout(resolve, delay));
      return fetchWithRetry(url, options, retries - 1, delay * 1.5); // Exponential backoff
    }
    
    return response;
  } catch (error) {
    if (retries > 0) {
      console.log(`Network error, retrying in ${delay}ms... (${retries} retries left)`);
      await new Promise(resolve => setTimeout(resolve, delay));
      return fetchWithRetry(url, options, retries - 1, delay * 1.5);
    }
    throw error;
  }
};

const CharacterDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [character, setCharacter] = useState(null);
  const [activeTab, setActiveTab] = useState("about");
  const [animeDetails, setAnimeDetails] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Placeholder backdrop image for characters
  const placeholderBackdrop = [process.env.REACT_APP_PLACEHOLDER_BACKDROP || 'https://res.cloudinary.com/dpz6ivjry/image/upload/v1741666344/740446_zkrklo.jpg'];
  
  // Function to handle clicking on an anime appearance
  const handleAnimeClick = (animeId) => {
    if (animeId) {
      navigate(`/anime/${animeId}`);
    }
  };
  
  // Handler for favorite status changes
  const handleFavoriteChange = (isFavorite) => {
    // You can update UI or state based on status changes if needed
  };

  // Helper function to extract data from both directly returned objects and paginated responses
  const extractData = (responseData) => {
    // If it's a direct object, return it
    if (responseData && !Array.isArray(responseData) && !responseData.data) {
      return responseData;
    }
    
    // If it's an array, it's using the old direct format
    if (Array.isArray(responseData)) {
      return responseData;
    }
    
    // If it has a data property that's an array or object, it's using the new format
    if (responseData && responseData.data) {
      return responseData.data;
    }
    
    // If it's none of these, return the original response
    return responseData;
  };

  useEffect(() => {
    let isMounted = true;
    
    const fetchData = async () => {
      try {
        if (!isMounted) return;
        setIsLoading(true);
        setError(null);
        
        // First, fetch the character details with retry logic
        const charResponse = await fetchWithRetry(`${API_BASE_URL}/api/characters/${id}`);
        
        if (!charResponse.ok) {
          throw new Error(`Error fetching character: ${charResponse.status}`);
        }
        
        const data = await charResponse.json();
        
        // Extract character data from either direct or paginated response
        const characterData = extractData(data);
        
        if (!isMounted) return;
        setCharacter(characterData);
        
        // If character has anime appearances, query for them
        if (characterData.anime && characterData.anime.length > 0) {
          // Get just a few anime to avoid rate limiting - max 5
          const firstFiveAnime = characterData.anime.slice(0, 5);
          
          // Extract all MAL IDs from character's anime appearances
          const malIds = firstFiveAnime.map(item => item.anime?.malId || item.anime?.mal_id || item.malId || item.mal_id).filter(Boolean);
          
          // Process anime in sequence to avoid rate limiting
          const animeMap = {};
          
          for (const malId of malIds) {
            try {
              // First, try direct lookup by MAL ID if available
              const directResponse = await fetchWithRetry(`${API_BASE_URL}/api/anime/mal/${malId}`);
              
              if (directResponse.ok) {
                const animeData = await directResponse.json();
                const extractedAnime = extractData(animeData);
                
                if (extractedAnime && extractedAnime.id && extractedAnime.malId) {
                  animeMap[extractedAnime.malId] = extractedAnime.id;
                }
                continue; // Skip to next anime if successful
              }
              
              // If direct lookup fails, fall back to search by title
              const animeRef = firstFiveAnime.find(a => 
                (a.anime?.malId === malId) || (a.malId === malId)
              );
              
              const title = animeRef?.anime?.title || animeRef?.name;
              
              if (!title) continue;
              
              // Add small delay to avoid rate limiting
              await new Promise(resolve => setTimeout(resolve, 300));
              
              const searchResponse = await fetchWithRetry(`${API_BASE_URL}/api/anime/search?query=${encodeURIComponent(title)}`);
              
              if (!searchResponse.ok) continue;
              
              const searchResults = await searchResponse.json();
              const searchData = extractData(searchResults);
              
              // Find the exact match by MAL ID
              const matchedAnime = Array.isArray(searchData) ? 
                searchData.find(anime => anime.malId === malId) : null;
                
              if (matchedAnime && matchedAnime.id) {
                animeMap[malId] = matchedAnime.id;
              }
            } catch (error) {
              console.error(`Error processing anime with MAL ID ${malId}:`, error);
              // Continue with next anime
            }
          }
          
          if (!isMounted) return;
          setAnimeDetails(animeMap);
        }
        
        if (!isMounted) return;
        setIsLoading(false);
      } catch (err) {
        console.error("Error loading data:", err);
        if (isMounted) {
          setError(err.message);
          setIsLoading(false);
        }
      }
    };
    
    fetchData();
    
    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, [id]);

  // Formatting helpers
  const formatDate = (dateString) => {
    if (!dateString) return "Present";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Loading state
  if (isLoading) return <LoadingSpinner />;
  
  // Error state
  if (error) return <ErrorMessage message={error} />;

  // Stats configuration for the StatsGrid component
  const statsConfig = [
    { label: "Favorites", value: character.favorites?.toLocaleString() || 'N/A' },
    { label: "Anime Appearances", value: character.anime?.length?.toLocaleString() || '0' },
    { label: "Manga Appearances", value: character.manga?.length?.toLocaleString() || '0' },
    { label: "Voice Actors", value: character.voices?.length?.toLocaleString() || '0' }
  ];

  // Tab configuration
  const tabs = [
    { id: 'about', label: 'About' },
    { id: 'appearances', label: 'Appearances' },
    { id: 'voices', label: 'Voice Actors' }
  ];

  return (
    <div className="character-details">
      {/* Hero Section using MediaHero */}
      <MediaHero backdrops={placeholderBackdrop}>
        <div className="hero-content">
          {/* Use custom poster div instead of MediaPoster */}
          <div className="poster">
            <img 
              src={character.images?.webp?.image_url || character.images?.webp?.imageUrl || character.images?.jpg?.image_url || character.images?.jpg?.imageUrl} 
              alt={character.name}
            />
            <FavoriteCharacterButton 
              characterId={character.id}
              onStatusChange={handleFavoriteChange}
            />
          </div>
          
          <div className="info">
            <MediaHeader 
              title={character.name}
              englishTitle={character.name_kanji}
              synonyms={character.nicknames}
            />

            <StatsGrid stats={statsConfig} />
          </div>
        </div>
      </MediaHero>

      {/* Main Content */}
      <div className="content-wrapper">
        <MediaTabs
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />

        {activeTab === 'about' && <CharacterAbout character={character} />}
        {activeTab === 'appearances' && (
          <CharacterAppearances 
            character={character}
            animeDetails={animeDetails}
            handleAnimeClick={handleAnimeClick}
          />
        )}
        {activeTab === 'voices' && <CharacterVoiceActors voices={character.voices} />}
      </div>
      
      {/* Additional Information Footer */}
      <div className="additional-info">
        <span>Added: {formatDate(character.created_at)}</span>
        <span>Last updated: {formatDate(character.updated_at)}</span>
      </div>
    </div>
  );
};

export default CharacterDetails;